/**
 * Instanced GPU skinning for the Legionnaire web prototype.
 *
 * - buildHumanoidGeometry(): procedural low-poly humanoid (~350 tris) in
 *   the anim.ts bind pose, with per-vertex skinIndex/skinWeight attributes.
 *   Rigid per-part binding (one bone per vertex) — reads fine at RTS
 *   distance and keeps the vertex shader to a tight loop.
 * - makeSkinnedMaterial(): MeshStandardMaterial patched via onBeforeCompile
 *   to do linear-blend skinning from the baked bone atlas (anim.ts), then
 *   the standard instanceMatrix transform. Lighting, shadows, fog, and
 *   instanceColor all keep working — only the vertex position/normal
 *   computation is replaced.
 * - Per-instance animation state rides on three InstancedBufferAttributes
 *   (aAnimA/aAnimB/aAnimBlend) that the render layer rewrites each frame
 *   from the formation/worker AnimControllers (a few floats per instance).
 * - buildPropGeometry(): tool props (hammer, saw) bound 100% to a hand
 *   bone, so props follow the hand through the same atlas with no extra
 *   CPU work.
 *
 * LOD policy lives with the callers (render/units.ts, render/workers.ts):
 * distant formations swap this geometry/material back to the static
 * capsule; only near formations pay skinning.
 */
import * as THREE from "three";
import { BONE_COUNT } from "./anim";

/** Body part: box dimensions, center, and the bone it is rigidly bound to. */
interface Part {
  w: number;
  h: number;
  d: number;
  x: number;
  y: number;
  z: number;
  bone: number;
}

// Bind pose matches BONE_OFFSETS in anim.ts (character faces +z).
const PARTS: Part[] = [
  // torso
  { w: 0.34, h: 0.2, d: 0.22, x: 0, y: 1.04, z: 0, bone: 0 }, // pelvis
  { w: 0.36, h: 0.3, d: 0.24, x: 0, y: 1.3, z: 0, bone: 2 }, // chest
  { w: 0.22, h: 0.24, d: 0.22, x: 0, y: 1.62, z: 0, bone: 3 }, // head
  // arms L
  { w: 0.11, h: 0.3, d: 0.11, x: -0.225, y: 1.3, z: 0, bone: 4 },
  { w: 0.09, h: 0.28, d: 0.09, x: -0.24, y: 1.02, z: 0, bone: 5 },
  { w: 0.08, h: 0.14, d: 0.08, x: -0.24, y: 0.85, z: 0, bone: 6 },
  // arms R
  { w: 0.11, h: 0.3, d: 0.11, x: 0.225, y: 1.3, z: 0, bone: 7 },
  { w: 0.09, h: 0.28, d: 0.09, x: 0.24, y: 1.02, z: 0, bone: 8 },
  { w: 0.08, h: 0.14, d: 0.08, x: 0.24, y: 0.85, z: 0, bone: 9 },
  // legs L
  { w: 0.13, h: 0.44, d: 0.13, x: -0.1, y: 0.76, z: 0, bone: 10 },
  { w: 0.11, h: 0.44, d: 0.11, x: -0.1, y: 0.33, z: 0, bone: 11 },
  { w: 0.11, h: 0.09, d: 0.26, x: -0.1, y: 0.045, z: 0.06, bone: 12 },
  // legs R
  { w: 0.13, h: 0.44, d: 0.13, x: 0.1, y: 0.76, z: 0, bone: 13 },
  { w: 0.11, h: 0.44, d: 0.11, x: 0.1, y: 0.33, z: 0, bone: 14 },
  { w: 0.11, h: 0.09, d: 0.26, x: 0.1, y: 0.045, z: 0.06, bone: 15 },
];

function pushBox(
  pos: number[],
  norm: number[],
  skinI: number[],
  skinW: number[],
  p: Part,
): void {
  const g = new THREE.BoxGeometry(p.w, p.h, p.d);
  g.translate(p.x, p.y, p.z);
  const ng = g.toNonIndexed();
  const pa = ng.getAttribute("position");
  const na = ng.getAttribute("normal");
  for (let i = 0; i < pa.count; i++) {
    pos.push(pa.getX(i), pa.getY(i), pa.getZ(i));
    norm.push(na.getX(i), na.getY(i), na.getZ(i));
    skinI.push(p.bone, 0, 0, 0);
    skinW.push(1, 0, 0, 0);
  }
  g.dispose();
  ng.dispose();
}

/**
 * Procedural placeholder humanoid. Non-indexed; each part is rigidly
 * bound to one bone. ~15 boxes x 12 tris = ~180 tris + head details.
 */
export function buildHumanoidGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const norm: number[] = [];
  const skinI: number[] = [];
  const skinW: number[] = [];
  for (const p of PARTS) pushBox(pos, norm, skinI, skinW, p);
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
  g.setAttribute("skinIndex", new THREE.Float32BufferAttribute(skinI, 4));
  g.setAttribute("skinWeight", new THREE.Float32BufferAttribute(skinW, 4));
  return g;
}

/**
 * Attach per-instance animation attributes to an (already instanced)
 * geometry. aAnimA = (clipSlot, frame), aAnimB = (clipSlot, frame),
 * aAnimBlend = mix factor A->B (0 = fully A).
 */
export function attachAnimAttributes(
  geo: THREE.BufferGeometry,
  capacity: number,
): {
  aAnimA: THREE.InstancedBufferAttribute;
  aAnimB: THREE.InstancedBufferAttribute;
  aAnimBlend: THREE.InstancedBufferAttribute;
} {
  const aAnimA = new THREE.InstancedBufferAttribute(
    new Float32Array(capacity * 2),
    2,
  );
  const aAnimB = new THREE.InstancedBufferAttribute(
    new Float32Array(capacity * 2),
    2,
  );
  const aAnimBlend = new THREE.InstancedBufferAttribute(
    new Float32Array(capacity),
    1,
  );
  for (const a of [aAnimA, aAnimB, aAnimBlend]) {
    a.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute(
      a === aAnimA ? "aAnimA" : a === aAnimB ? "aAnimB" : "aAnimBlend",
      a,
    );
  }
  return { aAnimA, aAnimB, aAnimBlend };
}

export interface AnimAttributes {
  aAnimA: THREE.InstancedBufferAttribute;
  aAnimB: THREE.InstancedBufferAttribute;
  aAnimBlend: THREE.InstancedBufferAttribute;
}

/** Write one instance's pose into the attribute arrays. */
export function writeAnimPose(
  attrs: AnimAttributes,
  index: number,
  slotA: number,
  frameA: number,
  slotB: number,
  frameB: number,
  blend: number,
): void {
  attrs.aAnimA.setXY(index, slotA, frameA);
  attrs.aAnimB.setXY(index, slotB, frameB);
  attrs.aAnimBlend.setX(index, blend);
}

export function flagAnimAttributes(attrs: AnimAttributes): void {
  attrs.aAnimA.needsUpdate = true;
  attrs.aAnimB.needsUpdate = true;
  attrs.aAnimBlend.needsUpdate = true;
}

/**
 * MeshStandardMaterial with instanced bone-atlas skinning.
 * uAtlasMeta = (boneCount, framesPerClip, atlasWidthTexels).
 * Clip slot selects a vertical band of framesPerClip rows; frame is a
 * float row offset within the band.
 */
export function makeSkinnedMaterial(
  atlas: THREE.DataTexture,
  boneCount: number,
  framesPerClip: number,
): THREE.MeshStandardMaterial {
  const mat = new THREE.MeshStandardMaterial({
    roughness: 0.85,
    metalness: 0.0,
  });
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uBoneAtlas = { value: atlas };
    shader.uniforms.uAtlasMeta = {
      value: new THREE.Vector3(
        boneCount,
        framesPerClip,
        atlas.image.width as number,
      ),
    };
    shader.vertexShader =
      `
      attribute vec4 skinIndex;
      attribute vec4 skinWeight;
      attribute vec2 aAnimA;
      attribute vec2 aAnimB;
      attribute float aAnimBlend;
      uniform sampler2D uBoneAtlas;
      uniform vec3 uAtlasMeta;
      mat4 fetchBone(float clip, float frame, float bone) {
        float x = bone * 4.0;
        float y = clip * uAtlasMeta.y + frame;
        vec4 c0 = texelFetch(uBoneAtlas, ivec2(int(x + 0.5), int(y + 0.5)), 0);
        vec4 c1 = texelFetch(uBoneAtlas, ivec2(int(x + 1.5), int(y + 0.5)), 0);
        vec4 c2 = texelFetch(uBoneAtlas, ivec2(int(x + 2.5), int(y + 0.5)), 0);
        vec4 c3 = texelFetch(uBoneAtlas, ivec2(int(x + 3.5), int(y + 0.5)), 0);
        return mat4(c0, c1, c2, c3);
      }
      mat4 blendBone(float bone) {
        mat4 a = fetchBone(aAnimA.x, aAnimA.y, bone);
        mat4 b = fetchBone(aAnimB.x, aAnimB.y, bone);
        // NB: mix(mat4, mat4, float) is WebGL2-only (GLSL ES 3.00); the
        // explicit form compiles on WebGL1 too.
        float t = aAnimBlend;
        return a * (1.0 - t) + b * t;
      }
      ` +
      shader.vertexShader
        .replace(
          "#include <beginnormal_vertex>",
          `
          vec3 objectNormal = vec3(0.0);
          for (int i = 0; i < 4; i++) {
            float w = skinWeight[i];
            if (w > 0.0) {
              objectNormal += mat3(blendBone(skinIndex[i])) * normal * w;
            }
          }
          `,
        )
        .replace(
          "#include <begin_vertex>",
          `
          vec3 transformed = vec3(0.0);
          for (int i = 0; i < 4; i++) {
            float w = skinWeight[i];
            if (w > 0.0) {
              transformed += (blendBone(skinIndex[i]) * vec4(position, 1.0)).xyz * w;
            }
          }
          `,
        );
  };
  // The patch is static; keep three from recompiling per material instance.
  mat.customProgramCacheKey = () => "legionnaire-skinned-v1";
  return mat;
}

/**
 * Tool prop geometry: a small box rigidly bound to one hand bone, so it
 * rides the hand through the atlas with no CPU work. Instance transforms
 * are the worker's own instance matrices.
 */
export function buildPropGeometry(
  kind: "hammer" | "saw",
  handBone: number,
): THREE.BufferGeometry {
  const pos: number[] = [];
  const norm: number[] = [];
  const skinI: number[] = [];
  const skinW: number[] = [];
  if (kind === "hammer") {
    // handle + head, held pointing forward from the fist
    pushBox(pos, norm, skinI, skinW, {
      w: 0.05, h: 0.05, d: 0.5, x: 0, y: -0.05, z: 0.25, bone: handBone,
    });
    pushBox(pos, norm, skinI, skinW, {
      w: 0.22, h: 0.1, d: 0.1, x: 0, y: -0.05, z: 0.5, bone: handBone,
    });
  } else {
    // saw blade
    pushBox(pos, norm, skinI, skinW, {
      w: 0.03, h: 0.12, d: 0.55, x: 0, y: -0.08, z: 0.28, bone: handBone,
    });
    pushBox(pos, norm, skinI, skinW, {
      w: 0.05, h: 0.05, d: 0.16, x: 0, y: -0.02, z: 0.02, bone: handBone,
    });
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute("normal", new THREE.Float32BufferAttribute(norm, 3));
  g.setAttribute("skinIndex", new THREE.Float32BufferAttribute(skinI, 4));
  g.setAttribute("skinWeight", new THREE.Float32BufferAttribute(skinW, 4));
  return g;
}

export { BONE_COUNT };
